/**
 * Sidebar recency filter — "show me the last N days".
 *
 * The sidebar grows forever; this hides the old material by default so the nav
 * reads as *recent work*. Nothing is ever made unreachable: the window is
 * user-changeable (including "All time"), search and direct links are untouched,
 * and the page you are currently viewing is pinned visible no matter its age.
 *
 * WHICH TIMESTAMP. `updatedAt` — the only date the /api/tree projection carries,
 * and the right one anyway: the API bumps it on create, on every editor save,
 * on `POST /api/publish` (so re-publishing a page counts as recent, which is how
 * most pages here are written), on archive/restore/trash, and on a drag that
 * re-parents the node. It deliberately does NOT move when a *sibling* is
 * re-ordered around it (see node-move.ts), so ordinary tidying doesn't reset
 * everyone's clock. `createdAt` exists on the stored document but is not
 * projected into the tree, and would wrongly bury a five-year-old page updated
 * this morning.
 *
 * A node with no (or an unparseable) `updatedAt` is treated as RECENT. Failing
 * open is the only safe direction: an undatable page must never silently vanish.
 *
 * FOLDERS AND NESTING. One uniform rule, applied to folders and to pages that
 * have children alike:
 *
 *     visible(n) = recent(n) OR any descendant of n is visible
 *
 * so an old folder holding one fresh page stays (you can reach the page), an old
 * folder holding only old pages goes, and a folder you created five seconds ago
 * stays even while empty. It is implemented by walking *up* from each recent
 * node and marking its ancestors, which handles arbitrary depth and terminates
 * on the cyclic parentIds the sidebar already tolerates.
 *
 * ARCHIVED NODES are not filtered at all — they render in the per-section
 * "Archived" group, which is collapsed by default and is already the deliberate
 * "old stuff lives here" bucket. They are excluded from `visible` and from the
 * hidden counts; the sidebar's archive render path bypasses this module.
 */

/** The windows offered in the UI, in days. */
export const RECENCY_CHOICES = [7, 14, 30, 90] as const;

/** The window applied when a user has never chosen one. */
export const DEFAULT_RECENCY_DAYS = 14;

/** A chosen window in days, or `null` for "All time" (filtering off). */
export type RecencyDays = number | null;

const DAY_MS = 86_400_000;

/** The subset of a tree node this module needs. `TreeNode` satisfies it. */
export interface RecencyNode {
  id: string;
  sectionId: string;
  isArchived: boolean;
  /** ISO 8601. Absent/unparseable is treated as "recent" — see the header note. */
  updatedAt?: string | null;
}

export interface RecencyResult {
  /**
   * Ids that survive the filter, ancestors included. Archived ids are absent —
   * the "Archived" group renders unfiltered and must not consult this set.
   */
  visible: Set<string>;
  /** How many live (non-archived) nodes the filter is hiding, in total… */
  hiddenCount: number;
  /**
   * …and per section, for the per-section "+N older" hint. Keyed by the node's
   * OWN sectionId, which for a cross-section-parented page is not necessarily
   * the section it renders under. Close enough for a count.
   */
  hiddenBySection: Map<string, number>;
  /** False when the window is "All time"; the caller can then skip every check. */
  filtering: boolean;
}

/** Coerce whatever came out of localStorage into a legal window. */
export function normalizeRecency(raw: unknown): RecencyDays {
  if (raw === null) return null; // an explicit "All time"
  if (typeof raw === 'number' && (RECENCY_CHOICES as readonly number[]).includes(raw)) return raw;
  return DEFAULT_RECENCY_DAYS;
}

function isRecent(updatedAt: string | null | undefined, cutoff: number): boolean {
  if (!updatedAt) return true;
  const t = Date.parse(updatedAt);
  return Number.isNaN(t) || t >= cutoff;
}

/**
 * Decide which nodes the sidebar should draw.
 *
 * @param nodes       every node in the tree (archived ones included; they're skipped).
 * @param parentById  node id -> its RENDERED parent id, or null when it renders at a
 *                    section root. Rendered, not raw `parentId`: the sidebar re-roots
 *                    orphans and cycle-trapped nodes, and this must agree with it.
 * @param days        the window, or null for "All time".
 * @param pinned      ids that stay visible regardless of age (the active document).
 * @param now         injectable clock, for tests.
 */
export function computeRecency(
  nodes: readonly RecencyNode[],
  parentById: ReadonlyMap<string, string | null>,
  days: RecencyDays,
  pinned: readonly string[] = [],
  now: number = Date.now()
): RecencyResult {
  const visible = new Set<string>();
  const hiddenBySection = new Map<string, number>();

  if (days == null) {
    for (const n of nodes) visible.add(n.id);
    return { visible, hiddenCount: 0, hiddenBySection, filtering: false };
  }

  const cutoff = now - days * DAY_MS;
  const live = nodes.filter((n) => !n.isArchived);

  // Mark a node and every ancestor above it. The `visible.has` check both avoids
  // re-walking a shared ancestor and terminates a parentId cycle (a->b->a), since
  // each id is added before we step to its parent.
  const reveal = (id: string): void => {
    let cur: string | null | undefined = id;
    while (cur && !visible.has(cur)) {
      visible.add(cur);
      cur = parentById.get(cur) ?? null;
    }
  };

  for (const n of live) if (isRecent(n.updatedAt, cutoff)) reveal(n.id);
  // The page being viewed (and its ancestors) survives any window — navigating to
  // an old page and watching it drop out of the nav is the worst version of this.
  for (const id of pinned) reveal(id);

  let hiddenCount = 0;
  for (const n of live) {
    if (visible.has(n.id)) continue;
    hiddenCount++;
    hiddenBySection.set(n.sectionId, (hiddenBySection.get(n.sectionId) ?? 0) + 1);
  }

  return { visible, hiddenCount, hiddenBySection, filtering: true };
}
